import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useSearchParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Typography,
  Link,
  Tooltip,
  TablePagination,
  TableSortLabel,
  FormControlLabel,
  Switch
} from "@mui/material";
import {
  EditOutlined,
  DeleteOutlined,
  ContentCopyOutlined,
  CheckCircleOutlined
} from "@mui/icons-material";
import { $fastOrderLinks } from "../../store/fastOrderLinks";
import { $modals } from "../../store/modals";
import { $snackbar } from "../../store/snackbar";
import config from "../../config";
import {
  FAST_ORDER_PAGE_SIZES,
  readFastOrderQuery,
  writeFastOrderQuery,
} from "../../store/fastOrderLinksQuery";

const FastOrderDiscountsTable = observer(() => {
  const [copiedLinkKey, setCopiedLinkKey] = useState("");
  const copyResetTimerRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentPage, perPage, onlyActive, sortBy, sortOrder } = readFastOrderQuery(searchParams);

  useEffect(() => {
    const normalizedParams = writeFastOrderQuery(searchParams, readFastOrderQuery(searchParams));
    if (normalizedParams.toString() !== searchParams.toString()) {
      setSearchParams(normalizedParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    $fastOrderLinks.setQuery({ currentPage, perPage, onlyActive, sortBy, sortOrder });
    $fastOrderLinks.fetchDiscounts();
  }, [currentPage, perPage, onlyActive, sortBy, sortOrder]);

  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
      }
    };
  }, []);

  const markCopied = (key) => {
    setCopiedLinkKey(key);

    if (copyResetTimerRef.current) {
      clearTimeout(copyResetTimerRef.current);
    }

    copyResetTimerRef.current = setTimeout(() => {
      setCopiedLinkKey("");
    }, 2000);
  };

  const handleEdit = (discount) => {
    $modals.editFastOrderDiscount.show();
    $modals.editFastOrderDiscount.setData(discount);
  };

  const handleDelete = (discount) => {
    $modals.deleteFastOrderDiscount.show();
    $modals.deleteFastOrderDiscount.setData(discount);
  };

  const handleChangePage = (_, page) => {
    updateQuery({ currentPage: page + 1 });
  };

  const handleChangeRowsPerPage = (event) => {
    updateQuery({ perPage: parseInt(event.target.value, 10), currentPage: 1 });
  };

  const updateQuery = (changes) => {
    setSearchParams(writeFastOrderQuery(searchParams, {
      currentPage, perPage, onlyActive, sortBy, sortOrder, ...changes,
    }));
  };

  const handleSort = (field) => {
    const isCurrentSort = sortBy === field;
    updateQuery({
      currentPage: 1,
      sortBy: isCurrentSort && sortOrder === "desc" ? "" : field,
      sortOrder: isCurrentSort && sortOrder === "asc" ? "desc" : "asc",
    });
  };

  const renderSortableHeader = (field, label) => {
    const active = sortBy === field;
    const nextAction = !active ? "по возрастанию" : sortOrder === "asc" ? "по убыванию" : "сбросить";

    return (
      <TableCell sortDirection={active ? sortOrder : false}>
        <TableSortLabel
          active={active}
          direction={active ? sortOrder : "asc"}
          onClick={() => handleSort(field)}
          aria-label={`${label}: ${nextAction}`}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  };

  // Функция для получения VK ссылки
  const getVkLink = (uuid) => {
    if (!uuid) return null;
    const appId = config.LARAVEL_API_URL === 'https://rpu.tovarbezpereplat.ru' ? '53586269' : '54194929';
    return `https://vk.ru/app${appId}#/fast_order?productId=${uuid}`;
  };

  // Функция для получения Telegram ссылки
  const getTelegramLink = (uuid) => {
    if (!uuid) return null;
    const isProduction = config.LARAVEL_API_URL === 'https://rpu.tovarbezpereplat.ru';
    return isProduction 
      ? `https://t.me/vprok_promo_bot/miniapp?startapp=productId_${uuid}`
      : `https://t.me/vprok_tester_bot/dev_tbp?startapp=productId_${uuid}`;
  };

  const getMaxLink = (uuid) => {
    if (!uuid) return null;
    const isProduction = config.LARAVEL_API_URL === 'https://rpu.tovarbezpereplat.ru';
    return isProduction
      ? `https://max.ru/id1650214890_bot?startapp=fastorder_${uuid}`
      : `https://max.ru/id1650212290_1_bot?startapp=fastorder_${uuid}`;
  };

  const handleCopyVkLink = async (uuid) => {
    if (!uuid) {
      $snackbar.show("UUID отсутствует", "error");
      return;
    }

    const link = getVkLink(uuid);
    
    try {
      await navigator.clipboard.writeText(link);
      markCopied(`vk-${uuid}`);
      $snackbar.show("VK ссылка скопирована", "success");
    } catch (error) {
      console.error("Ошибка при копировании:", error);
      $snackbar.show("Ошибка при копировании VK ссылки", "error");
    }
  };

  const handleCopyTelegramLink = async (uuid) => {
    if (!uuid) {
      $snackbar.show("UUID отсутствует", "error");
      return;
    }

    const link = getTelegramLink(uuid);
    
    try {
      await navigator.clipboard.writeText(link);
      markCopied(`tg-${uuid}`);
      $snackbar.show("Telegram ссылка скопирована", "success");
    } catch (error) {
      console.error("Ошибка при копировании:", error);
      $snackbar.show("Ошибка при копировании Telegram ссылки", "error");
    }
  };

  const handleCopyMaxLink = async (uuid) => {
    if (!uuid) {
      $snackbar.show("UUID отсутствует", "error");
      return;
    }

    const link = getMaxLink(uuid);

    if (!link) {
      $snackbar.show("MAX ссылка недоступна", "error");
      return;
    }

    try {
      await navigator.clipboard.writeText(link);
      markCopied(`max-${uuid}`);
      $snackbar.show("MAX ссылка скопирована", "success");
    } catch (error) {
      console.error("Ошибка при копировании:", error);
      $snackbar.show("Ошибка при копировании MAX ссылки", "error");
    }
  };

  const renderCopyAction = (key, label, link, onClick) => {
    if (!link) {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );
    }

    const isCopied = copiedLinkKey === key;

    return (
      <Tooltip title={link} arrow>
        <Box
          component="button"
          type="button"
          onClick={onClick}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            border: 0,
            cursor: "pointer",
            font: "inherit",
            appearance: "none",
            color: isCopied ? "success.main" : "primary.main",
            backgroundColor: isCopied ? "rgba(46, 125, 50, 0.08)" : "transparent",
            transition: "background-color 0.2s ease, color 0.2s ease",
            "&:hover": {
              backgroundColor: isCopied ? "rgba(46, 125, 50, 0.12)" : "action.hover"
            }
          }}
        >
          {isCopied ? (
            <CheckCircleOutlined sx={{ fontSize: 18 }} />
          ) : (
            <ContentCopyOutlined sx={{ fontSize: 18 }} />
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: "inherit",
              whiteSpace: "nowrap"
            }}
          >
            {isCopied ? "Скопировано" : label}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("ru-RU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const isActive = (discount) => {
    const now = new Date();
    const beginAt = discount.begin_at ? new Date(discount.begin_at) : null;
    const endAt = discount.end_at ? new Date(discount.end_at) : null;
    
    // Проверяем удаление
    if (discount.deleted_at) return false;
    
    // Проверяем диапазон дат
    if (beginAt && now < beginAt) return false; // Еще не началась
    if (endAt && now > endAt) return false; // Уже закончилась
    
    return true;
  };

  if (
    $fastOrderLinks.loading ||
    $fastOrderLinks.currentPage !== currentPage ||
    $fastOrderLinks.perPage !== perPage ||
    $fastOrderLinks.onlyActive !== onlyActive ||
    $fastOrderLinks.sortBy !== sortBy ||
    $fastOrderLinks.sortOrder !== sortOrder
  ) {
    return null; // Спиннер уже показывается глобально
  }

  return (
    <TableContainer component={Paper}>
      <Box sx={{ px: 2, py: 1 }}>
        <FormControlLabel
          control={(
            <Switch
              checked={onlyActive}
              onChange={(event) => updateQuery({ onlyActive: event.target.checked, currentPage: 1 })}
            />
          )}
          label="Только активные"
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>UUID</TableCell>
            <TableCell>Ссылка (ВК)</TableCell>
            <TableCell>Ссылка (ТГ)</TableCell>
            <TableCell>Ссылка (MAX)</TableCell>
            {renderSortableHeader("product_id", "Товар")}
            {renderSortableHeader("original_price", "Цена в ТБП")}
            {renderSortableHeader("discount_price", "Цена со скидкой")}
            {renderSortableHeader("sales_limit", "Лимит продаж")}
            <TableCell>Дата создания</TableCell>
            <TableCell>Дата начала</TableCell>
            <TableCell>Дата окончания</TableCell>
            <TableCell>Активность</TableCell>
            {renderSortableHeader("max_count", "Доступно к покупке")}
            <TableCell>Скрыть старую цену</TableCell>
            <TableCell>Розничная цена</TableCell>
            <TableCell align="center">Действия</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {$fastOrderLinks.discounts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={16} align="center">
                Скидки не найдены
              </TableCell>
            </TableRow>
          ) : $fastOrderLinks.discounts.map((discount) => (
            <TableRow key={discount.id}>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {discount.uuid || "-"}
                </Typography>
              </TableCell>
              <TableCell>
                {renderCopyAction(
                  `vk-${discount.uuid}`,
                  "ВК",
                  getVkLink(discount.uuid),
                  () => handleCopyVkLink(discount.uuid)
                )}
              </TableCell>
              <TableCell>
                {renderCopyAction(
                  `tg-${discount.uuid}`,
                  "ТГ",
                  getTelegramLink(discount.uuid),
                  () => handleCopyTelegramLink(discount.uuid)
                )}
              </TableCell>
              <TableCell>
                {renderCopyAction(
                  `max-${discount.uuid}`,
                  "MAX",
                  getMaxLink(discount.uuid),
                  () => handleCopyMaxLink(discount.uuid)
                )}
              </TableCell>
              <TableCell>
                {discount.product_id ? (
                  <Link
                    href={`https://tovarbezpereplat.ru/products/${discount.product_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    color="primary"
                  >
                    {discount.product_id}
                  </Link>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell>{discount.original_price ? `${discount.original_price} ₽` : "-"}</TableCell>
              <TableCell>
                {discount.discount_price && discount.original_price ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.primary" fontWeight="bold">
                      {discount.discount_price} ₽
                    </Typography>
                    <Typography variant="body2" color="error.main" fontWeight="bold">
                      {Math.round(((discount.original_price - discount.discount_price) / discount.original_price) * 100)}%
                    </Typography>
                  </Box>
                ) : discount.discount_price ? (
                  <Typography variant="body2" color="text.primary" fontWeight="bold">
                    {discount.discount_price} ₽
                  </Typography>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell>{discount.sales_limit ?? "-"}</TableCell>
              <TableCell>{formatDate(discount.created_at)}</TableCell>
              <TableCell>{formatDate(discount.begin_at)}</TableCell>
              <TableCell>{formatDate(discount.end_at)}</TableCell>
              <TableCell>
                <Chip 
                  label={isActive(discount) ? "Да" : "Нет"} 
                  color={isActive(discount) ? "success" : "error"}
                  size="small" 
                />
              </TableCell>
              <TableCell>
                {discount.max_count}
              </TableCell>
              <TableCell>
                <Chip
                  label={discount.hide_old_price ? "Да" : "Нет"}
                  color={discount.hide_old_price ? "warning" : "default"}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={discount.use_retail_price ? "Да" : "Нет"}
                  color={discount.use_retail_price ? "info" : "default"}
                  size="small"
                />
              </TableCell>
              <TableCell align="center">
                <IconButton
                  size="small"
                  onClick={() => handleEdit(discount)}
                  color="primary"
                >
                  <EditOutlined />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(discount)}
                  color="error"
                >
                  <DeleteOutlined />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={$fastOrderLinks.total}
        page={currentPage - 1}
        rowsPerPage={perPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={FAST_ORDER_PAGE_SIZES}
        labelRowsPerPage="Строк на странице"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} из ${count !== -1 ? count : `более ${to}`}`
        }
        getItemAriaLabel={(type) => ({
          first: "Первая страница",
          last: "Последняя страница",
          next: "Следующая страница",
          previous: "Предыдущая страница",
        }[type])}
      />
    </TableContainer>
  );
});

export default FastOrderDiscountsTable;
